import * as React from "react";
import {format} from "date-fns";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import {useAppSelector, store} from "../../redux/store";
import {postRequest, setHeader} from "repository/genericRepository";
import {INDIVIDUALPAYMENTREPORT_ENDPOINT} from "commonUtils/globalAPISettings";
import {FormButton} from "components/FormComponents/Button/FormButton";
import {Loader} from "components/FormComponents/Loader/Loader";
import {
  handleAxiosErrors,
  initialMessage,
  isValidObject,
  loggedMessage,
  message,
} from "commonUtils/utils";
import DialogTitleCommon, {DialogCloseCommon} from "./DialogTitleCommon";
import {
  Alerts,
  API_TIMEOUTS_MILLISECONDS,
  CPSConstants,
  PageNames,
} from "commonUtils/enumerations";
import {logMessage} from "services/loggerService";
import type {
  HeaderRecordProps,
  RecordsDataProps,
} from "./IndividualPaymentLimitsReportColumns";
import {IndividualPaymentLimitsReportColumns} from "./IndividualPaymentLimitsReportColumns";
import IndividualPaymentReportGrid from "components/Grids/IndividualPaymentReportGrid";
import {useForm} from "react-hook-form";
import FormValidationAlert from "components/FormComponents/Alert/FormValidationAlert";
import {GridColDef} from "@mui/x-data-grid";

interface FormValues {
  pageIndex: number;
  pageSize: number | string | undefined;
  columnfield: string;
  sortcolumn: string;
}

interface Props {
  reportopen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  buttononename: string;
  buttontwoname: string;
}

const ReportsDialog: React.FC<Props> = ({
  reportopen,
  setOpen,
  buttononename,
  buttontwoname,
}) => {
  useForm<FormValues>({
    defaultValues: {
      pageIndex: 0,
      pageSize: CPSConstants.PAGE_SIZE,
      columnfield: "Product",
      sortcolumn: "desc",
    },
  });

  const {userDetails} = useAppSelector(state => {
    return state.auth;
  });

  const initialPageSize: string | null | undefined =
    sessionStorage.getItem("DefautGridPageSize") ??
    String(CPSConstants.PAGE_SIZE);
  const parseData = JSON.parse(initialPageSize);
  const [paginationModel, setPaginationModel] = React.useState<{
    page: number;
    pageSize: number;
  }>({
    page: 0,
    pageSize: parseData?.pageSize ?? CPSConstants.PAGE_SIZE,
  });
  const [sortModel, setSortModel] = React.useState<
    [
      {
        field: string;
        sort: string;
      },
    ]
  >([{field: "product", sort: "desc"}]);
  const [records, getRecords] = React.useState<RecordsDataProps[]>([]);
  const [totalrecordscount, setTotalRecordsCount] = React.useState<number>(1);
  const [loader, setLoader] = React.useState<boolean>(false);
  const [datagridheadermedia, setDataGridHeaderMedia] =
    React.useState<number>(0);
  const [datagridheight, setDataGridHeight] = React.useState<string>("");
  const [dataGridColumns, setDataGridColumns] = React.useState<
    HeaderRecordProps[]
  >([]);
  const [showAlert, setShowAlert] = React.useState<boolean>(false);
  const [serverValidation, setServerValidation] =
    React.useState<message>(initialMessage);

  React.useEffect(() => {
    IndividualPaymentLimitsReportColumns({
      setDataGridColumns,
      setDataGridHeight,
      setDataGridHeaderMedia,
    });
    const data = {
      pageIndex: paginationModel?.page,
      pageSize: paginationModel?.pageSize,
      columnfield: sortModel[0]?.field,
      sortcolumn: sortModel[0]?.sort,
    };
    LoadPaymentLimitsReport(data);
  }, [
    paginationModel?.page,
    paginationModel?.pageSize,
    sortModel[0]?.field,
    sortModel[0]?.sort,
  ]);

  const handlePaginationModalChange = (newPaginationModel: {
    pageSize: number;
    page: number;
  }) => {
    setPaginationModel(newPaginationModel);
    sessionStorage.setItem(
      "DefautGridPageSize",
      JSON.stringify(newPaginationModel),
    );
  };

  const handleClose = () => {
    logMessage(
      "INFO",
      `User ${userDetails?.userName} has closed the Individual PaymentLimit Report dialog.`,
      PageNames.LOGIN,
    );
    setOpen(false);
  };

  const LoadPaymentLimitsReport = React.useMemo(
    () => async (data: FormValues) => {
      try {
        const {pageIndex, pageSize, columnfield, sortcolumn} = data;
        const authState = store.getState();
        const APIHeaders = {
          Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
        };
        const sortCol = columnfield ?? "product";
        const sortOrder = sortcolumn ?? "desc";
        const isDownloadToExcel = false;
        setLoader(true);
        const responseData = await postRequest({
          url: INDIVIDUALPAYMENTREPORT_ENDPOINT,
          headers: setHeader(APIHeaders),
          params: {
            pageIndex: isValidObject(pageIndex) ? pageIndex + 1 : 1,
            pageSize: pageSize ?? CPSConstants.PAGE_SIZE,
            sortCol,
            sortOrder,
            isDownloadToExcel,
          },
          data: {},
        });
        if (isValidObject(responseData)) {
          getRecords(responseData?.report);
          setTotalRecordsCount(responseData?.totalRecordsCount);
        }
        setLoader(false);
      } catch (error: any) {
        loggedMessage(error, serverValidation?.message, PageNames.DASHBOARD);
        handleAxiosErrors(error, setShowAlert, setServerValidation);
        setLoader(false);
      }
    },
    [records],
  );

  const downloadExcelBase64 = async (
    base64String: string,
    fileName: string,
  ) => {
    try {
      const linkSource = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64String}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
    } catch (error: any) {
      setLoader(false);
      loggedMessage(error, serverValidation?.message, PageNames.DASHBOARD);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      logMessage(
        "INFO",
        `User ${userDetails?.userName} has downloaded the Individual PaymentLimit Report.`,
        PageNames.DASHBOARD,
      );
      const authState = store.getState();
      const APIHeaders = {
        Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
      };
      const pageIndex = 1;
      const pageSize = 0;
      const sortCol = "";
      const sortOrder = "";
      const isDownloadToExcel = true;
      const responseData = await postRequest({
        url: INDIVIDUALPAYMENTREPORT_ENDPOINT,
        headers: setHeader(APIHeaders),
        timeout: API_TIMEOUTS_MILLISECONDS.INDIVIDUALPAYMENTREPORT_ENDPOINT,
        params: {pageIndex, pageSize, sortCol, sortOrder, isDownloadToExcel},
        data: {},
      });
      if (isValidObject(responseData)) {
        downloadExcelBase64(
          responseData?.file,
          `IndividualPaymentLimitReport_${format(
            new Date(),
            "MMddyyyyHHmms",
          )}.xlsx`,
        );
      }
    } catch (error: any) {
      loggedMessage(error, serverValidation?.message, PageNames.DASHBOARD);
      handleAxiosErrors(error, setShowAlert, setServerValidation);
      setLoader(false);
    }
  };

  const columns: HeaderRecordProps | GridColDef[] | any[] = dataGridColumns;

  return (
    <>
      <Loader open={loader} />
      {Object.keys(records)?.length > 0 && (
        <Dialog maxWidth="xl" open={reportopen}>
          <DialogTitleCommon title="Individual Payment Limits Report" />
          <DialogCloseCommon onClick={handleClose} />
          <DialogContent className="individual-payment-dialog-container">
            {showAlert && serverValidation?.message && (
              <Box pb={1}>
                <FormValidationAlert
                  closeIcon={true}
                  showAlertBox={showAlert}
                  setShowAlertBox={setShowAlert}
                  severity={
                    (serverValidation?.isSuccessMessage ?? false)
                      ? Alerts.SUCCESS
                      : (serverValidation?.isValidationMessage ?? false)
                        ? Alerts.INFO
                        : Alerts.ERROR
                  }
                  message={serverValidation?.message}
                />
              </Box>
            )}
            {records ? (
              <Box height={datagridheight} minHeight={datagridheight}>
                <IndividualPaymentReportGrid
                  datagridheadermedia={datagridheadermedia}
                  columns={columns}
                  records={records}
                  totalrecordscount={totalrecordscount}
                  hideFooter={false}
                  loader={loader}
                  paginationModel={paginationModel}
                  onSortModelChange={setSortModel}
                  onPaginationModelChange={handlePaginationModalChange}
                />
              </Box>
            ) : (
              "Records not found."
            )}
            <Box>
              <Stack
                sx={{justifyContent: "center", marginTop: "0.5rem"}}
                spacing={1}
                direction="row">
                <FormButton
                  onClick={() => {
                    handleDownloadExcel();
                  }}
                  size="small"
                  type="submit"
                  variant="contained"
                  outlet={buttononename}
                />
                <FormButton
                  onClick={handleClose}
                  size="small"
                  type="submit"
                  variant="contained"
                  outlet={buttontwoname}
                />
              </Stack>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default ReportsDialog;
